import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Check, ChevronsUpDown, Copy, Loader2, MoveRight, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { useAllTenants } from "@/hooks/use-tenants";
import { CellTenantsUnavailable } from "@/components/cells/cell-failures-alert";
import { tenantLabel, tenantOptionValue } from "@/lib/cells/cells";
import {
  useDesks,
  useTicketCount,
  useCatalogCategories,
  useDeskTags,
  useDeskEmployees,
  useDeskCannedResponses,
  useDeskAutomationSettings,
  useBulkTransferTickets,
  useBulkMoveCatalogItems,
  useCopyDeskEmployees,
  useCopyDeskTags,
  useWorkflowsReferencingDesk,
  useDeleteDesk,
} from "@/hooks/use-desk-migration";
import type { CatalogItemSummary, DeskTag } from "@/hooks/use-desk-migration";

export const Route = createFileRoute("/desk-migration")({
  component: DeskMigrationPage,
});

function DeskMigrationPage() {
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenantPopoverOpen, setTenantPopoverOpen] = useState(false);
  const [originDeskId, setOriginDeskId] = useState<string>("");
  const [targetDeskId, setTargetDeskId] = useState<string>("");

  const { data: tenants, isLoading: tenantsLoading, failures: tenantFailures } = useAllTenants();

  const { data: desks, isLoading: desksLoading } = useDesks(selectedTenantId);
  const { data: ticketCount, isLoading: ticketsLoading } = useTicketCount(selectedTenantId, originDeskId);
  const { data: catalogData, isLoading: catalogLoading } = useCatalogCategories(selectedTenantId);
  const { data: originTags } = useDeskTags(selectedTenantId, originDeskId);
  const { data: targetTags } = useDeskTags(selectedTenantId, targetDeskId);
  const { data: originEmployees } = useDeskEmployees(selectedTenantId, originDeskId);
  const { data: targetEmployees } = useDeskEmployees(selectedTenantId, targetDeskId);
  const { data: workflowsReferencingDesk } = useWorkflowsReferencingDesk(selectedTenantId, originDeskId);
  const { data: cannedResponsesCount } = useDeskCannedResponses(selectedTenantId, originDeskId);
  const { data: automationSettings } = useDeskAutomationSettings(selectedTenantId, originDeskId);

  const sortedTenants = useMemo(() => {
    if (!tenants) return [];
    return [...tenants].sort((a, b) => a.company.localeCompare(b.company));
  }, [tenants]);

  const selectedTenant = tenants?.find((t) => t.id === selectedTenantId);
  const originDesk = useMemo(() => desks?.find((d) => d.id === originDeskId), [desks, originDeskId]);
  const targetDesk = useMemo(() => desks?.find((d) => d.id === targetDeskId), [desks, targetDeskId]);

  const catalogItemsInOriginDesk: CatalogItemSummary[] = useMemo(() => {
    if (!catalogData || !originDeskId) return [];
    return catalogData.categories.flatMap((cat) =>
      (cat.items ?? []).filter((item) => item.desk_id === originDeskId),
    );
  }, [catalogData, originDeskId]);

  const missingTags: DeskTag[] = useMemo(() => {
    if (!originTags || !targetTags) return [];
    const targetTagNames = new Set(targetTags.map((t) => t.name.toLowerCase()));
    return originTags.filter((t) => !targetTagNames.has(t.name.toLowerCase()));
  }, [originTags, targetTags]);

  const membersNotInTarget: string[] = useMemo(() => {
    if (!originEmployees || !targetEmployees) return [];
    const targetSet = new Set(targetEmployees);
    return originEmployees.filter((id) => !targetSet.has(id));
  }, [originEmployees, targetEmployees]);

  const transferTickets = useBulkTransferTickets();
  const moveCatalogItems = useBulkMoveCatalogItems();
  const copyEmployees = useCopyDeskEmployees();
  const copyTags = useCopyDeskTags();
  const deleteDesk = useDeleteDesk();

  const handleTransferTickets = async () => {
    if (!ticketCount || ticketCount === 0) return;
    const result = await transferTickets.mutateAsync({ tenantId: selectedTenantId, deskId: originDeskId, targetDeskId });
    if (result.failed > 0) {
      toast.warning(`Transferred ${result.succeeded} tickets, ${result.failed} failed`);
    } else {
      toast.success(`Transferred ${result.succeeded} tickets to target desk`);
    }
  };

  const handleDeleteDesk = async () => {
    try {
      await deleteDesk.mutateAsync({ tenantId: selectedTenantId, deskId: originDeskId });
      toast.success(`Deleted desk ${originDesk?.name}`);
      setOriginDeskId("");
      setTargetDeskId("");
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        toast.error("Cannot delete desk — it still has resources. Move all tickets, catalog items, and other resources first.");
      } else {
        toast.error("Failed to delete desk");
      }
    }
  };

  const handleCopyTags = async () => {
    if (missingTags.length === 0) return;
    const result = await copyTags.mutateAsync({ tenantId: selectedTenantId, targetDeskId, tags: missingTags });
    if (result.failed > 0) {
      toast.warning(`Copied ${result.succeeded} tags, ${result.failed} failed`);
    } else {
      toast.success(`Copied ${result.succeeded} tags to target desk`);
    }
  };

  const handleCopyMembers = async () => {
    if (membersNotInTarget.length === 0) return;
    const merged = [...new Set([...(targetEmployees ?? []), ...membersNotInTarget])];
    await copyEmployees.mutateAsync({ tenantId: selectedTenantId, targetDeskId, employeeIds: merged });
    toast.success(`Added ${membersNotInTarget.length} members to target desk`);
  };

  const handleMoveCatalogItems = async () => {
    if (catalogItemsInOriginDesk.length === 0) return;
    const itemIds = catalogItemsInOriginDesk.map((item) => item.id);
    const result = await moveCatalogItems.mutateAsync({ tenantId: selectedTenantId, itemIds, targetDeskId });
    if (result.failed > 0) {
      toast.warning(`Moved ${result.succeeded} catalog items, ${result.failed} failed`);
    } else {
      toast.success(`Moved ${result.succeeded} catalog items to target desk`);
    }
  };

  const handleTenantChange = (tenantId: string) => {
    setSelectedTenantId(tenantId);
    setOriginDeskId("");
    setTargetDeskId("");
    setTenantPopoverOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Desk Migration</h1>
        <p className="text-muted-foreground">
          Migrate all tickets and service catalog items from one desk to another, with the option to
          delete the origin desk afterwards.
        </p>
      </div>
      <Separator />

      <div className="mx-auto max-w-4xl space-y-6">
        {/* Tenant Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Select Tenant</CardTitle>
            <CardDescription>
              Choose the tenant whose desks you want to migrate.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Popover open={tenantPopoverOpen} onOpenChange={setTenantPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantPopoverOpen}
                  className="w-[300px] justify-between"
                >
                  <span className="truncate">
                    {tenantsLoading
                      ? "Loading..."
                      : selectedTenant
                        ? tenantLabel(selectedTenant)
                        : "Select tenant"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <Command>
                  <CellTenantsUnavailable failures={tenantFailures} />
                  <CommandInput placeholder="Search tenants..." />
                  <CommandList className="max-h-[300px]">
                    <CommandEmpty>No tenant found.</CommandEmpty>
                    <CommandGroup>
                      {sortedTenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenantOptionValue(tenant)}
                          onSelect={() => { handleTenantChange(tenant.id); }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedTenantId === tenant.id ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {tenantLabel(tenant)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </CardContent>
        </Card>

        {/* Desk Selection */}
        {selectedTenantId && (
          <Card>
            <CardHeader>
              <CardTitle>Select Desks</CardTitle>
              <CardDescription>
                Choose the origin desk to migrate from and the target desk to migrate to.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-4">
                <div className="space-y-2">
                  <Label>Origin Desk</Label>
                  <Select
                    value={originDeskId}
                    onValueChange={(value) => {
                      setOriginDeskId(value);
                      if (value === targetDeskId) setTargetDeskId("");
                    }}
                    disabled={desksLoading}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={desksLoading ? "Loading..." : "Select origin desk"} />
                    </SelectTrigger>
                    <SelectContent>
                      {desks?.map((desk) => (
                        <SelectItem key={desk.id} value={desk.id}>
                          {desk.emoji} {desk.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <ArrowRight className="mb-2 h-5 w-5 text-muted-foreground" />

                <div className="space-y-2">
                  <Label>Target Desk</Label>
                  <Select
                    value={targetDeskId}
                    onValueChange={(value) => {
                      setTargetDeskId(value);
                    }}
                    disabled={!originDeskId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select target desk" />
                    </SelectTrigger>
                    <SelectContent>
                      {desks
                        ?.filter((d) => d.id !== originDeskId)
                        .map((desk) => (
                          <SelectItem key={desk.id} value={desk.id}>
                            {desk.emoji} {desk.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Impact Summary */}
        {originDeskId && (
          <Card>
            <CardHeader>
              <CardTitle>Migration Impact</CardTitle>
              <CardDescription>
                Resources that will be moved from{" "}
                <strong>
                  {originDesk?.emoji} {originDesk?.name}
                </strong>{" "}
                to{" "}
                <strong>
                  {targetDesk ? `${targetDesk.emoji} ${targetDesk.name}` : "..."}
                </strong>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Assignee / Members warning */}
              {targetDeskId && membersNotInTarget.length > 0 && (
                <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-orange-800 dark:text-orange-200">
                          {membersNotInTarget.length} member{membersNotInTarget.length !== 1 ? "s" : ""} not in target desk
                        </p>
                        <p className="text-sm text-orange-700 dark:text-orange-300">
                          Their tickets will be <strong>unassigned</strong> after migration unless
                          you add them to the target desk first.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleCopyMembers()}
                        disabled={copyEmployees.isPending}
                        className="shrink-0"
                      >
                        {copyEmployees.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Users className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Add Members
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Tickets */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="font-medium">Tickets</p>
                  <p className="text-sm text-muted-foreground">
                    All tickets will be transferred to the target desk
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {targetDeskId && ticketCount != null && ticketCount > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => void handleTransferTickets()}
                      disabled={transferTickets.isPending}
                    >
                      {transferTickets.isPending ? (
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <MoveRight className="mr-1.5 h-3.5 w-3.5" />
                      )}
                      Transfer
                    </Button>
                  )}
                  <Badge variant={ticketCount && ticketCount > 0 ? "default" : "secondary"}>
                    {ticketsLoading ? "..." : (ticketCount ?? 0)}
                  </Badge>
                </div>
              </div>

              {/* Catalog Items */}
              <div className="space-y-3">
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">Service Catalog Items</p>
                    <p className="text-sm text-muted-foreground">
                      Catalog items will be reassigned to the target desk
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {targetDeskId && catalogItemsInOriginDesk.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleMoveCatalogItems()}
                        disabled={moveCatalogItems.isPending}
                      >
                        {moveCatalogItems.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <MoveRight className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Move Items
                      </Button>
                    )}
                    <Badge variant={catalogItemsInOriginDesk.length > 0 ? "default" : "secondary"}>
                      {catalogLoading ? "..." : catalogItemsInOriginDesk.length}
                    </Badge>
                  </div>
                </div>

                {catalogItemsInOriginDesk.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Visible</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {catalogItemsInOriginDesk.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {item.icon && <span>{item.icon}</span>}
                              <span>{item.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.ticket_type}</Badge>
                          </TableCell>
                          <TableCell>{item.is_visible ? "Yes" : "No"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Tags warning */}
              {targetDeskId && missingTags.length > 0 && (
                <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium text-orange-800 dark:text-orange-200">
                          {missingTags.length} tag{missingTags.length !== 1 ? "s" : ""} not in target desk
                        </p>
                        <p className="text-sm text-orange-700 dark:text-orange-300">
                          Tickets using these tags will lose their tag associations after migration.
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleCopyTags()}
                        disabled={copyTags.isPending}
                        className="shrink-0"
                      >
                        {copyTags.isPending ? (
                          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Copy className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Copy Tags
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {missingTags.map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="outline"
                          style={tag.color ? { borderColor: tag.color, color: tag.color } : undefined}
                        >
                          {tag.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Workflows / automation warning */}
              {workflowsReferencingDesk && workflowsReferencingDesk.length > 0 && (
                <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
                  <div>
                    <p className="font-medium text-orange-800 dark:text-orange-200">
                      {workflowsReferencingDesk.length} workflow{workflowsReferencingDesk.length !== 1 ? "s" : ""} reference this desk
                    </p>
                    <p className="text-sm text-orange-700 dark:text-orange-300">
                      These workflows will <strong>not</strong> be automatically updated
                      after migration. Manually update their desk configuration.
                    </p>
                    <ul className="mt-2 space-y-1 text-sm text-orange-700 dark:text-orange-300">
                      {workflowsReferencingDesk.map((wf) => (
                        <li key={wf.id} className="flex items-center gap-2">
                          <span>{wf.name}</span>
                          {!wf.enabled && (
                            <Badge variant="outline" className="text-xs">disabled</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Canned responses warning */}
              {cannedResponsesCount != null && cannedResponsesCount > 0 && (
                <div className="flex gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      {cannedResponsesCount} canned response{cannedResponsesCount !== 1 ? "s" : ""}
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      Pre-written reply templates for agents. These are desk-specific and will{" "}
                      <strong>not</strong> be migrated — recreate them in the target desk if needed.
                    </p>
                  </div>
                </div>
              )}

              {/* Automation settings warning */}
              {automationSettings && automationSettings.length > 0 && (
                <div className="flex gap-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-600" />
                  <div>
                    <p className="font-medium text-yellow-800 dark:text-yellow-200">
                      Active automation settings
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      The following automations are enabled on this desk and will{" "}
                      <strong>not</strong> be migrated: {automationSettings.join(", ")}.
                      Configure them on the target desk manually.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Desk */}
        {originDeskId && (
          <div className="flex items-center justify-between rounded-lg border border-destructive/30 p-3">
            <div>
              <p className="font-medium">Delete Origin Desk</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete{" "}
                <strong>
                  {originDesk?.emoji} {originDesk?.name}
                </strong>{" "}
                and all its configuration
              </p>
            </div>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => void handleDeleteDesk()}
              disabled={deleteDesk.isPending}
            >
              {deleteDesk.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              )}
              Delete Desk
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
